import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { LoadingState, ErrorState, PageHeader } from '@/components/ui'
import { SapDocumentView, type SapDocumentType } from '@/components/sap/SapDocumentView'
import { customerApi } from '../lib/api'

type EndpointType = '/sap/quotations' | '/sap/orders' | '/sap/deliveries' | '/sap/invoices' | '/sap/credits'

interface Props {
  title: string
  endpoint: EndpointType
}

const ENDPOINT_TO_TYPE: Record<EndpointType, SapDocumentType> = {
  '/sap/quotations': 'quotation',
  '/sap/orders':     'order',
  '/sap/deliveries': 'delivery',
  '/sap/invoices':   'invoice',
  '/sap/credits':    'credit',
}

export function CustomerDocumentDetailPage({ title, endpoint }: Props) {
  const { docEntry } = useParams()
  const [doc, setDoc] = useState<Record<string, unknown> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)

  useEffect(() => {
    if (!docEntry) return
    customerApi.get<Record<string, unknown>>(`${endpoint}/${docEntry}`)
      .then((res) => setDoc(res.data))
      .catch((e: Error & { backendCode?: string }) => {
        setError(e.message)
        setErrorCode(e.backendCode ?? null)
      })
      .finally(() => setLoading(false))
  }, [endpoint, docEntry])

  if (loading) return <LoadingState />
  if (error) {
    const linkMissing =
      errorCode === 'CUSTOMER_NO_SAP_LINK' ||
      errorCode === 'CUSTOMER_NO_TANSS_LINK' ||
      errorCode === 'CUSTOMER_NO_TANSS_NUMBER' ||
      errorCode === 'CUSTOMER_NO_TANSS_CREDENTIALS' ||
      errorCode === 'CUSTOMER_NO_TENANT'
    return (
      <ErrorState
        title={linkMissing ? 'Kein SAP-Konto verknüpft' : 'Fehler beim Laden'}
        description={
          linkMissing
            ? 'Dein Benutzerkonto ist noch keinem SAP-Kunden zugeordnet. Bitte wende dich an deinen Ansprechpartner bei implec.'
            : undefined
        }
        details={error}
      />
    )
  }
  if (!doc) return null

  const docNum = String(doc.DocNum ?? doc.DocEntry ?? '')

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={`${title} #${docNum}`} />
      <SapDocumentView doc={doc} type={ENDPOINT_TO_TYPE[endpoint]} />
    </div>
  )
}
